import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Users, FileText, Activity, Trash2, BellRing, MessageSquareHeart, Star, Reply } from "lucide-react";
import api, { formatApiError } from "../lib/axiosClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { BrandMark } from "../components/Brand";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const StatCard = ({ label, value, accent }) => (
  <div
    className="rounded-2xl bg-white border border-[#E8E2D9] p-5"
    data-testid={`admin-stat-${label.toLowerCase().replace(/\W+/g, "-")}`}
  >
    <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8782] font-semibold">{label}</p>
    <p className="font-serif-display text-3xl text-[#2D2A26] font-semibold mt-2" style={{ color: accent || "#2D2A26" }}>
      {value}
    </p>
  </div>
);

const Admin = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null); // { id, email } | null
  const [deleting, setDeleting] = useState(false);
  const [runningReminders, setRunningReminders] = useState(false);

  const loadAll = async () => {
    try {
      const [s, r, f] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/submissions"),
        api.get("/admin/feedback"),
      ]);
      setStats(s.data); setRows(r.data); setFeedback(f.data);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load admin data."));
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      await loadAll();
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/submissions/${toDelete.id}`);
      toast.success("Submission deleted.");
      setToDelete(null);
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't delete that submission."));
    } finally {
      setDeleting(false);
    }
  };

  const runReminders = async () => {
    setRunningReminders(true);
    try {
      const { data } = await api.post("/admin/reminders/run-now");
      toast.success(`Reminder sweep done. ${data.sent} email${data.sent === 1 ? "" : "s"} sent.`);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't run reminder sweep."));
    } finally {
      setRunningReminders(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#C86B5E]" />
      </div>
    );
  }

  const totalCount = stats?.total_submissions || 0;
  const pct = (n) => (totalCount ? Math.round((n / totalCount) * 100) : 0);

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-6 py-10 sm:py-14">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <BrandMark className="h-8 w-auto" />
                <span className="h-6 w-px bg-[#E8E2D9]" aria-hidden="true" />
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.8} />
                  <span className="text-xs uppercase tracking-[0.18em] text-[#8A8782] font-semibold">Admin overview</span>
                </div>
              </div>
              <h1 className="font-serif-display text-3xl sm:text-4xl text-[#2D2A26] font-semibold">
                Welcome, {user?.name || "Admin"}.
              </h1>
              <p className="text-sm text-[#8A8782] mt-1">{user?.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={runReminders}
                disabled={runningReminders}
                data-testid="admin-run-reminders-button"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-[#2D2A26] border-2 border-[#E8E2D9] hover:border-[#C86B5E] rounded-full px-4 py-2 bg-white transition-all disabled:opacity-60"
              >
                {runningReminders ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
                Run reminders
              </button>
              <Link
                to="/"
                className="text-sm text-[#5C5A56] hover:text-[#2D2A26] transition-colors"
              >
                ← Back to site
              </Link>
              <button
                onClick={logout}
                data-testid="admin-logout-button"
                className="text-sm font-medium text-[#2D2A26] border-2 border-[#E8E2D9] hover:border-[#D1C7B8] rounded-full px-4 py-2 bg-white transition-all"
              >
                Sign out
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          <StatCard label="Total users" value={stats?.total_users ?? "—"} />
          <StatCard label="Submissions" value={stats?.total_submissions ?? "—"} accent="#C86B5E" />
          <StatCard label="Couple sessions" value={stats?.total_couples ?? "—"} accent="#8A9A86" />
          <StatCard label="Strong / Mixed"
            value={`${stats?.classification_breakdown?.strong ?? 0} / ${stats?.classification_breakdown?.mixed ?? 0}`}
          />
        </div>

        {/* Quadrant breakdown */}
        <div className="mt-8 rounded-2xl bg-white border border-[#E8E2D9] p-6 sm:p-7">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-[#C86B5E]" />
            <h2 className="font-serif-display text-xl text-[#2D2A26] font-semibold">Quadrant breakdown</h2>
          </div>
          <div className="grid sm:grid-cols-4 gap-3">
            {[
              { k: "thriving", label: "Collaborative & Thriving", bg: "#EBF2EA" },
              { k: "stable",   label: "Stable but Disconnected",  bg: "#F7F5F0" },
              { k: "caring",   label: "Caring but Reactive",      bg: "#FDF5EE" },
              { k: "strained", label: "Strained & Adversarial",   bg: "#F3F4F6" },
            ].map((q) => {
              const n = stats?.quadrant_breakdown?.[q.k] ?? 0;
              return (
                <div key={q.k} className="rounded-xl p-4" style={{ background: q.bg }}>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8782] font-semibold">{q.label}</p>
                  <p className="font-serif-display text-2xl text-[#2D2A26] font-semibold mt-2">
                    {n}
                    <span className="text-sm text-[#8A8782] font-normal ml-2">({pct(n)}%)</span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Submissions table */}
        <div className="mt-8 rounded-2xl bg-white border border-[#E8E2D9] overflow-hidden">
          <div className="flex items-center gap-2 p-6 pb-4">
            <Users className="w-4 h-4 text-[#C86B5E]" />
            <h2 className="font-serif-display text-xl text-[#2D2A26] font-semibold">
              Submissions <span className="text-sm text-[#8A8782] font-normal">({rows?.length || 0})</span>
            </h2>
          </div>
          <div className="overflow-x-auto" data-testid="admin-submissions-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FDF5EE]">
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold">Date</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold">User</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold">Pattern</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold">Connection</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold">Collaboration</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold">Report</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold w-12">Delete</th>
                </tr>
              </thead>
              <tbody>
                {(rows || []).map((r) => (
                  <tr key={r.id} className="border-t border-[#E8E2D9] hover:bg-[#FDFBF7] transition-colors">
                    <td className="px-4 py-3 text-[#5C5A56]">
                      {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </td>
                    <td className="px-4 py-3 text-[#2D2A26]">{r.user_email || <span className="text-[#8A8782] italic">anonymous</span>}</td>
                    <td className="px-4 py-3">
                      <span
                        className={[
                          "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold",
                          r.classification === "mixed"
                            ? "bg-[#F7F5F0] text-[#5C5A56]"
                            : "bg-[#F8EBE9] text-[#C86B5E]",
                        ].join(" ")}
                      >
                        {r.pattern_label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-[#2D2A26] font-semibold">{Math.round(r.emotional_connection_pct)}%</td>
                    <td className="px-4 py-3 text-right text-[#2D2A26] font-semibold">{Math.round(r.collaborative_conflict_pct)}%</td>
                    <td className="px-4 py-3 text-right">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/submissions/${r.id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`admin-download-pdf-${r.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#C86B5E] hover:underline"
                      >
                        <FileText className="w-3.5 h-3.5" /> PDF
                      </a>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setToDelete({ id: r.id, email: r.user_email || "anonymous" })}
                        data-testid={`admin-delete-button-${r.id}`}
                        aria-label="Delete submission"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[#8A8782] hover:text-[#C86B5E] hover:bg-[#FDF5EE] transition-colors"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* Feedback */}
        <div className="mt-8 rounded-2xl bg-white border border-[#E8E2D9] overflow-hidden">
          <div className="flex items-center gap-2 p-6 pb-4">
            <MessageSquareHeart className="w-4 h-4 text-[#C86B5E]" />
            <h2 className="font-serif-display text-xl text-[#2D2A26] font-semibold">
              User feedback <span className="text-sm text-[#8A8782] font-normal">({feedback.length})</span>
            </h2>
          </div>
          {feedback.length === 0 ? (
            <p className="px-6 pb-6 text-sm text-[#8A8782] italic">No feedback yet.</p>
          ) : (
            <div className="divide-y divide-[#E8E2D9]" data-testid="admin-feedback-list">
              {feedback.map((f) => {
                const mailtoHref = f.email
                  ? `mailto:${f.email}?subject=${encodeURIComponent(
                      "Re: your feedback on the Relationship Check-in"
                    )}&body=${encodeURIComponent(
                      `Hi ${f.name || "there"},\n\nThank you for taking the time to share your feedback with us. You wrote:\n\n` +
                      f.message.split("\n").map((l) => `> ${l}`).join("\n") +
                      `\n\n— Common Ground Institute`
                    )}`
                  : null;
                return (
                <div key={f.id} className="p-6 hover:bg-[#FDFBF7] transition-colors" data-testid={`admin-feedback-item-${f.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      {f.rating && (
                        <span className="inline-flex items-center gap-0.5">
                          {[1,2,3,4,5].map((n) => (
                            <Star
                              key={n}
                              className="w-3.5 h-3.5"
                              fill={f.rating >= n ? "#E8C766" : "none"}
                              stroke={f.rating >= n ? "#B89A3E" : "#D1C7B8"}
                              strokeWidth={1.5}
                            />
                          ))}
                        </span>
                      )}
                      <span className="text-sm font-semibold text-[#2D2A26]">
                        {f.name || <span className="italic text-[#8A8782]">Anonymous</span>}
                      </span>
                      {f.email && <span className="text-xs text-[#8A8782]">· {f.email}</span>}
                      {f.page && (
                        <span className="text-[10px] uppercase tracking-wider text-[#8A8782] bg-[#F7F5F0] px-2 py-0.5 rounded-full font-semibold">
                          {f.page}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-[#8A8782]">
                        {new Date(f.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                      {mailtoHref && (
                        <a
                          href={mailtoHref}
                          data-testid={`admin-feedback-reply-${f.id}`}
                          aria-label={`Reply to ${f.email}`}
                          title={`Reply to ${f.email}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#C86B5E] hover:text-white hover:bg-[#C86B5E] border-2 border-[#F8EBE9] hover:border-[#C86B5E] rounded-full px-3 py-1 transition-all"
                        >
                          <Reply className="w-3.5 h-3.5" />
                          Reply
                        </a>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-[#2D2A26] leading-relaxed whitespace-pre-wrap">{f.message}</p>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="bg-[#FDFBF7] border-[#E8E2D9]" data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#2D2A26]">
              Delete submission?
            </DialogTitle>
            <DialogDescription className="text-[#5C5A56]">
              This permanently removes the submission for{" "}
              <span className="font-semibold text-[#2D2A26]">{toDelete?.email}</span>. Any couple
              invite that references it will be unlinked. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button
              onClick={() => setToDelete(null)}
              data-testid="admin-delete-cancel"
              className="px-5 py-2.5 rounded-full border-2 border-[#E8E2D9] hover:border-[#D1C7B8] text-sm font-medium bg-white text-[#2D2A26] transition-all"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="admin-delete-confirm"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#C86B5E] text-white text-sm font-medium hover:bg-[#B35B4E] disabled:opacity-60 transition-all"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
